import { FACTOR_ENUMS } from 'pages/RedesignDashboard/constants';
import {
  defaultPaymentHistoryModulesSortPriority,
  defaultCreditUsageModulesSortPriority,
  defaultCreditAgeModulesSortPriority,
  defaultAccountMixModulesSortPriority,
  defaultCreditInquiryModulesSortPriority,
} from './sortPriorities';
import { evaluateTodayContainerModules } from '../helpers/dashboard.helper';
import { isEmpty } from 'helpers/string.helper';

const sortModules = (factorModules, sortPriority) => {
  const orderedFactorModules = factorModules.sort((a, b) => {
    const b_priority = sortPriority[b.module] ?? 10000;
    const a_priority = sortPriority[a.module] ?? 10000;
    return a_priority - b_priority;
  });
  return orderedFactorModules;
};

export const createModulesContainer = deps => {
  const {
    PAYMENT_HISTORY,
    ACCOUNT_MIX,
    CREDIT_AGE,
    CREDIT_INQUIRIES,
    CREDIT_USAGE,
    DEFAULT,
    POTENTIAL_CREDIT_PROFILE,
  } = FACTOR_ENUMS;

  const {
    selectedFactor,
    todayContainer,
    todayContainerLoading,
    paymentHistoryContainer,
    paymentHistoryContainerLoading,
    creditAgeContainer,
    creditAgeContainerLoading,
    accountMixContainer,
    accountMixContainerLoading,
    creditUsageContainer,
    creditUsageContainerLoading,
    creditInquiriesContainer,
    creditInquiriesContainerLoading,
    potentialCreditProfileContainer,
    potentialCreditProfileContainerLoading,
    config,
    excludeEngagementOffers,
    plusBannerOverviewWebConfig,
    showCapitalOneOffers,
    removeSesamePickOffers,
    showModuleAvailableForYou,
  } = deps;

  const transformModuleName = module => {
    const moduleMappings = {
      'Personal Loan Prequal': 'PERSONAL_LOAN_REDESIGN',
      AUTO_REFINANCE: 'AUTO_REFINANCE_REDESIGN',
      AUTO_LOAN: 'AUTO_LOAN_REDESIGN',
      Insurance: 'INSURANCE_REDESIGN',
      MORTGAGE: 'HOME_LOAN_REDESIGN',
      'Suggested Credit Card': 'SUGGESTED_CREDIT_CARD_REDESIGN',
      'Partner Text Disclaimer': 'PARTNER_TEXT_DISCLAIMER',
      'Explore Offers': 'EXPLORE_OFFERS_REDESIGN',
      'Featured Ad': 'NATIVE_ADS',
    };
    return moduleMappings[module] || module;
  };

  const modulesToUse = () => {
    let factorModules = [];
    let isFactorContainerLoading = false;
    let todayContainerModules = [];

    switch (selectedFactor) {
      case DEFAULT:
        if (todayContainer?.modules) {
          const mappedModules = todayContainer?.modules?.map(item => ({
            ...item,
            module: transformModuleName(item.module),
          }));
          todayContainerModules?.push(...mappedModules);

          const creditToolsModule = todayContainer?.modules?.filter(
            mod => mod?.module === 'CREDIT_TOOLS',
          );
          const availableForYouModule = todayContainer?.modules?.filter(
            mod => mod?.module === 'AVAILABLE_FOR_YOU',
          );

          if (showModuleAvailableForYou && !isEmpty(availableForYouModule)) {
            factorModules?.push(...availableForYouModule);
          } else {
            factorModules?.push(...creditToolsModule);
          }
          if (!showCapitalOneOffers) {
            todayContainerModules = todayContainerModules?.filter(
              obj => obj?.module !== 'PRESCREEN_OFFERS',
            );
          }
          if (!showModuleAvailableForYou) {
            todayContainerModules = todayContainerModules?.filter(
              obj => obj?.module !== 'AVAILABLE_FOR_YOU',
            );
          }
          if (excludeEngagementOffers) {
            todayContainerModules = todayContainerModules?.filter(
              obj => obj?.module !== 'PROFILE_PAGE_EVENT_OFFER',
            );
          }

          const prescreenModule = todayContainerModules?.find(
            obj => obj?.module === 'PRESCREEN_OFFERS',
          );
          if (prescreenModule?.offers?.length > 0) {
            todayContainerModules = todayContainerModules?.filter(
              obj => obj?.module !== 'PROFILE_PAGE_EVENT_OFFER',
            );
          }

          const hasPlusPickCreditCardModules = todayContainerModules?.find(
            obj => obj?.module === 'PLUS_PICK_CREDIT_CARD',
          );

          if (
            !isEmpty(hasPlusPickCreditCardModules) &&
            removeSesamePickOffers
          ) {
            todayContainerModules = todayContainerModules?.filter(
              obj => obj?.module !== 'PLUS_PICK_CREDIT_CARD',
            );
          }
        }

        todayContainerModules = todayContainerModules?.filter(
          module => module?.module !== 'RENT_REPORTING_PREMIUM_UPSELL_BANNER',
        );

        todayContainerModules = evaluateTodayContainerModules({
          todayContainerModules,
          plusBannerOverviewWebConfig,
        });
        isFactorContainerLoading = todayContainerLoading;
        break;
      case PAYMENT_HISTORY:
        if (paymentHistoryContainer?.modules) {
          factorModules?.push(...paymentHistoryContainer?.modules);
        }
        if (config?.showCreditCardReview) {
          factorModules?.push({
            module: 'REVIEW_ENTRY_POINTS',
          });
        }
        isFactorContainerLoading = paymentHistoryContainerLoading;
        factorModules = sortModules(
          factorModules,
          defaultPaymentHistoryModulesSortPriority,
        );
        break;
      case CREDIT_AGE:
        if (creditAgeContainer?.modules) {
          factorModules?.push(...creditAgeContainer?.modules);
        }
        factorModules?.unshift({ module: 'NOTIFICATIONS' });

        isFactorContainerLoading = creditAgeContainerLoading;
        factorModules = sortModules(
          factorModules,
          defaultCreditAgeModulesSortPriority,
        );
        break;
      case ACCOUNT_MIX:
        if (accountMixContainer?.modules) {
          const modules = accountMixContainer?.modules;
          const accountMixUpdatedModules = modules.map(moduleObj => {
            if (moduleObj.module === 'TRADELINE_CREDIT_CARDS') {
              return {
                ...moduleObj,
                module: 'TRADELINE_ACCOUNT_MIX_CREDIT_CARDS',
              };
            }
            if (moduleObj.module === 'Featured Ad') {
              return {
                ...moduleObj,
                module: 'NATIVE_ADS',
              };
            }
            return moduleObj;
          });
          accountMixContainer.modules = accountMixUpdatedModules;
          if (accountMixContainer?.modules) {
            factorModules?.push(...accountMixContainer?.modules);
          }
          factorModules?.push({ module: 'ACCOUNT_MIX_OFFERS' });
          if (config?.showCreditCardReview) {
            factorModules?.push({ module: 'REVIEW_ENTRY_POINTS' });
          }
        }
        isFactorContainerLoading = accountMixContainerLoading;
        factorModules = sortModules(
          factorModules,
          defaultAccountMixModulesSortPriority,
        );
        break;
      case CREDIT_USAGE:
        if (creditUsageContainer?.modules) {
          factorModules?.push(...creditUsageContainer?.modules);
          if (config?.showCreditCardReview) {
            factorModules?.push({ module: 'REVIEW_ENTRY_POINTS' });
          }
        }
        isFactorContainerLoading = creditUsageContainerLoading;
        factorModules = sortModules(
          factorModules,
          defaultCreditUsageModulesSortPriority,
        );
        break;
      case CREDIT_INQUIRIES:
        if (creditInquiriesContainer?.modules) {
          factorModules?.push(...creditInquiriesContainer?.modules);
        }
        factorModules?.push({ module: 'CREDIT_INQUIRIES_OFFERS' });
        isFactorContainerLoading = creditInquiriesContainerLoading;
        factorModules = sortModules(
          factorModules,
          defaultCreditInquiryModulesSortPriority,
        );
        break;
      case POTENTIAL_CREDIT_PROFILE:
        isFactorContainerLoading = potentialCreditProfileContainerLoading;

        if (potentialCreditProfileContainer?.modules) {
          const potentialModules = potentialCreditProfileContainer?.modules;
          factorModules?.push(...potentialModules);
        }
        break;
      default:
        break;
    }

    if (
      selectedFactor !== DEFAULT &&
      factorModules?.length > 0 &&
      selectedFactor !== POTENTIAL_CREDIT_PROFILE
    ) {
      factorModules?.push({
        module: 'FACTORS_FREQUENTLY_ASKED_QUESTIONS',
        frequentlyAskedQuestion:
          config?.frequentlyAskedQuestion?.[selectedFactor],
      });
      factorModules?.push({
        module: 'GO_TO_FULL_CREDIT_REPORT',
      });
    }
    return { factorModules, isFactorContainerLoading, todayContainerModules };
  };

  const factorContainers = modulesToUse();

  return { ...factorContainers };
};
